import 'flip';

import { ChatCopyInfo } from '@agent/agent-interface/contact-view/chat/model/chat-copy-info';
import { ChatCopyService } from '@agent/agent-interface/contact-view/chat/services/chat-copy.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, Subscription } from 'rxjs';

import { UntilDestroy, untilDestroyed } from '@contact/upgrade';

import { ChatAssistanceService } from '../chat-assistance.service';
import { IChatMessage } from '../models/chat-message';
import { IChatRoom } from '../models/chat-room';

@UntilDestroy()
@Component({
  selector: 'chat-window',
  templateUrl: 'chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
})
export class ChatWindowComponent implements OnInit, OnDestroy {
  roomId: string | undefined;
  confId: string | undefined;
  messages: IChatMessage[] = [];
  copyInfo: ChatCopyInfo | undefined;
  private copySubscription: Subscription;
  private roomCount: number;

  constructor(private readonly chatAssistanceService: ChatAssistanceService, private readonly copyService: ChatCopyService) {}

  get rooms(): IChatRoom[] {
    return this.chatAssistanceService.getRooms();
  }

  get nbUnreadMessages(): number {
    return this.chatAssistanceService.nbUnreadMessages();
  }

  get flipped() {
    return this.chatAssistanceService.isFlipped;
  }

  ngOnInit(): void {
    const rooms = this.chatAssistanceService.getRooms();
    if (rooms.length > 0) {
      this.onRoomSelected(rooms[0].id);
    }
    const cardElement = $('#card') as any;
    cardElement.flip({ trigger: 'manual' });

    this.roomCount = this.chatAssistanceService.getRooms().length;
    this.chatAssistanceService.isFlipped = this.roomCount === 0;
    cardElement.flip(this.chatAssistanceService.isFlipped);

    this.subscribeToCopyMessage();
  }

  tabLabel(room: IChatRoom): string {
    return room.toGuests.map(guest => `${guest.firstName} ${guest.lastName}`).join('\r\n');
  }

  roomParticipantCount(room: IChatRoom): number {
    return room.toGuests.length;
  }

  disableAddRoomButton(): boolean {
    return this.chatAssistanceService.isFlipped || this.rooms.length === 4;
  }

  newRoom(): void {
    ($('#card') as any).flip('toggle');
    this.chatAssistanceService.isFlipped = !this.chatAssistanceService.isFlipped;
  }

  onNewRoom(toGuestId: any): void {
    let roomId = this.chatAssistanceService.create();

    this.chatAssistanceService
      .getGuestsDirectory()
      .pipe(
        map(directory => directory.filter(guest => guest.isPresent)),
        map(directory => directory.filter(guest => guest.userId === toGuestId.userId)),
        untilDestroyed(this)
      )
      .subscribe(
        directory => {
          if (this.chatAssistanceService.getGuests(roomId).length === 0 && directory.length === 1) {
            this.chatAssistanceService.addGuest(roomId, directory[0]);
          }
        },
        () => {},
        () => {
          this.onRoomSelected(roomId);
          this.sendCopyInfoIfExist(roomId);
        }
      );
  }

  onRoomSelected(roomId: string): void {
    this.roomId = roomId;
  }

  onRoomClosed(roomId: string): void {
    this.chatAssistanceService.close(roomId);
    if (this.roomId && this.roomId === roomId) {
      this.roomId = undefined;
    }
    this.flipIfNoRooms();
  }

  onAddGuestToRoom(roomId: string) {
    this.confId = roomId;
    ($('#card') as any).flip('toggle');
    this.chatAssistanceService.isFlipped = !this.chatAssistanceService.isFlipped;
  }

  onLeaveConferenceList() {
    this.confId = undefined;
  }

  ngOnDestroy() {
    this.copySubscription.unsubscribe();
    this.copyService.copyInfo$.next(undefined);
  }

  private flipIfNoRooms() {
    if (this.chatAssistanceService.getRooms().length === 0) {
      this.chatAssistanceService.isFlipped = true;
      ($('#card') as any).flip(this.chatAssistanceService.isFlipped);
    }
  }

  private sendCopyInfoIfExist(roomId) {
    if (this.copyInfo) {
      this.copyInfo.copyMessages.forEach(msg => this.replaceImageAttributes(msg.content));
      this.sendMsg(0, this.copyInfo.copyMessages, roomId, this.copyInfo.copyKey);
      this.copyInfo = undefined;
    }
  }

  private subscribeToCopyMessage() {
    this.copySubscription = this.copyService.copyInfo$.subscribe(result => {
      if (result) {
        if (result.roomId) {
          this.roomId = result.roomId;
          this.onRoomSelected(result.roomId);
          this.sendMsg(0, result.copyMessages, result.roomId, result.copyKey);
          this.chatAssistanceService.isFlipped = false;
        } else {
          this.copyInfo = result;
          this.chatAssistanceService.isFlipped = true;
        }
        ($('#card') as any).flip(this.chatAssistanceService.isFlipped);
      }
    });
  }

  private sendMsg(index, copyMessages, roomId, copyKey) {
    if (copyMessages.length > index) {
      setTimeout(() => {
        copyMessages.forEach(msg => this.replaceImageAttributes(msg.content));
        copyMessages[index].copy.isFullCopy = copyMessages[index].isFullCopy;
        copyMessages[index].copy.copyKey = copyKey;
        this.chatAssistanceService.send(roomId, copyMessages[index].content, copyMessages[index].copy);
        this.sendMsg(++index, copyMessages, roomId, copyKey);
      }, 10);
    }
  }

  private replaceImageAttributes(textContent: string) {
    return textContent.replace(/<img.*?alt="(.*?)"[^\>]+>/g, x => {
      let div = document.createElement('div');
      div.innerHTML = x;
      return ':' + div.getElementsByTagName('img')[0].alt + ':';
    });
  }
}
